import { defineCommands, type CommandsOf } from './defineCommands';
import type { CanvasStoreContext, CanvasLayer } from '../types';
import type { CanvasEntity } from '../entityTypes';

export const layerCommands = defineCommands({
  'layer.add': (ctx: CanvasStoreContext, event: { layer: CanvasLayer }): CanvasStoreContext => {
    if (ctx.layers.some(l => l.id === event.layer.id)) return ctx;
    return {
      ...ctx,
      layers: [...ctx.layers, event.layer],
    };
  },

  'layer.remove': (ctx: CanvasStoreContext, event: { layerId: string }): CanvasStoreContext => {
    if (ctx.layers.length <= 1) return ctx;
    const remaining = ctx.layers.filter(l => l.id !== event.layerId);
    if (remaining.length === ctx.layers.length) return ctx;
    const fallbackLayerId = remaining[0].id;

    const reassignRecord = (record: Record<string, CanvasEntity>): Record<string, CanvasEntity> => {
      const result: Record<string, CanvasEntity> = {};
      for (const [id, e] of Object.entries(record)) {
        result[id] = e.layerId === event.layerId ? { ...e, layerId: fallbackLayerId } : e;
      }
      return result;
    };

    return {
      ...ctx,
      layers: remaining,
      nodes: reassignRecord(ctx.nodes),
      connectors: reassignRecord(ctx.connectors),
      groups: reassignRecord(ctx.groups),
    };
  },

  'layer.update': (ctx: CanvasStoreContext, event: {
    layerId: string;
    changes: Partial<Pick<CanvasLayer, 'name' | 'visible' | 'locked'>>;
  }): CanvasStoreContext => ({
    ...ctx,
    layers: ctx.layers.map(l =>
      l.id === event.layerId ? { ...l, ...event.changes } : l,
    ),
  }),

  'layer.reorder': (ctx: CanvasStoreContext, event: { layerIds: string[] }): CanvasStoreContext => {
    const layerMap = new Map(ctx.layers.map(l => [l.id, l]));
    const reordered: CanvasLayer[] = [];
    for (const id of event.layerIds) {
      const l = layerMap.get(id);
      if (l) reordered.push(l);
    }
    for (const l of ctx.layers) {
      if (!event.layerIds.includes(l.id)) reordered.push(l);
    }
    return { ...ctx, layers: reordered };
  },

  'layer.setActive': (ctx: CanvasStoreContext, event: { layerId: string }): CanvasStoreContext => ({
    ...ctx,
    activeLayerId: event.layerId,
  }),
});

export type LayerCommand = CommandsOf<typeof layerCommands>;
